import {
  Component,
  inject,
  Input,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  AfterViewInit,
} from '@angular/core';
import { LogoutComponent } from '../../../pages/auth/components/logout/logout.component';
import { SearchBoxComponent } from '../../../pages/search/components/search-box/search-box.component';
import { AuthService } from '../../../pages/auth/services/auth.service';
import { NavigationEnd, Router } from '@angular/router';
import { NotificationsService } from '../../../pages/notifications/services/notifications.service';
import { FileUploadService } from '../../../pages/documents/services/file-upload.service';
import { ImageService } from '../../services/image-service/image.service';
import {
  filter,
  finalize,
  interval,
  Subscription,
  switchMap,
  takeUntil,
} from 'rxjs';
import { ClickOutsideDirective } from '../../directives/click-outside/click-outside.directive';
import { InvitePopupComponent } from '../invite-popup/invite-popup.component';

import { AsyncPipe } from '@angular/common';
import { getBaseRoute } from '../../utils/utils';
import { TourService } from '../../services/tour-service/tour.service';
import { DynamicPopupComponent } from '../dynamic-popup/dynamic-popup.component';
import { LoggedInUser } from '../../models/logged-in-user';
import { AuditLogService } from '../../services/audit-log/audit-log.service';
import { LoaderService } from '../../services/loader/loader.service';
import { CommonService } from '../../services/common/common.service';
import { ToastType } from '../../interfaces/common';
import { DestroyBase } from '../../base-classes/destroy-component-base';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    LogoutComponent,
    SearchBoxComponent,
    ClickOutsideDirective,
    InvitePopupComponent,
    AsyncPipe,
    DynamicPopupComponent,
    SvgIconComponent,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent
  extends DestroyBase
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input({ required: false }) isUnauthorizedLayout = false;
  @Output() uploadProgressToggled = new EventEmitter<void>();

  authService = inject(AuthService);
  router = inject(Router);
  notificationsService = inject(NotificationsService);
  fileUploadService = inject(FileUploadService);
  imageService = inject(ImageService);
  tourService = inject(TourService);
  auditLogService = inject(AuditLogService);
  loaderService = inject(LoaderService);
  commonService = inject(CommonService);
  name: string | undefined;
  proPic: string | undefined;
  profileImageUrl$ = this.imageService.profileImageUrl$;

  connection_display_name: string | undefined;
  hasUnreadNotifications = false;
  unreadNotificationCount = 0;
  shouldShake = false;
  notificationCheckSubscription?: Subscription;
  routerSubscription?: Subscription;
  reAuthSubscription?: Subscription;
  showDropdown = false;
  showInvitePopup = false;
  showTourPopup = false;
  userInfo!: LoggedInUser | null;
  showVerifyPopup = false;
  isVerifying = false;
  readonly isSmeUser = this.authService.isSMEUser();
  readonly isDHUser = this.authService.isDHUser();
  get activeUploadsCount(): number {
    return this.fileUploadService.activeUploadsCount;
  }

  get hasUploads(): boolean {
    return this.fileUploadService.uploads$.value.length > 0;
  }

  isSubscriptionPopupOpen = false;

  ngOnInit(): void {
    this.userInfo = this.authService.getLoggedInUserInfo();
    this.name = this.userInfo?.name;
    this.commonService.connectionName$
      .pipe(takeUntil(this.destroy$))
      .subscribe((name) => {
        if (name) {
          this.connection_display_name =
            name ?? this.userInfo?.connection_display_name;
        }
      });
    if (this.name) {
      this.proPic = this.name.charAt(0).toUpperCase();
    }

    this.commonService.userName$
      .pipe(takeUntil(this.destroy$))
      .subscribe((name) => {
        if (name) {
          this.name = name;
          this.proPic = this.name.charAt(0).toUpperCase();
        }
      });

    if (!this.isUnauthorizedLayout) {
      this.imageService.initialize(this.isSmeUser);
    }

    this.checkUnreadNotifications();
    this.startNotificationChecking();
  }

  ngAfterViewInit(): void {
    this.tourService.updateTourSteps(getBaseRoute(this.router.url));
    this.routerSubscription = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.tourService.updateTourSteps(getBaseRoute(event.urlAfterRedirects));
      });
    const tourComplete = this.userInfo?.existing_user;

    if (!tourComplete) {
      this.openTourPopup();
    }
  }

  startPageTour(): void {
    this.closeTourPopup();
    this.tourService.onHelp();
    this.auditLogService.logFeature({
      featureName: 'Tour guide',
      action: 'Start page tour',
      meta: { pageUrl: this.router.url },
      description: `Page tour of ${this.router.url} triggered`,
    });
  }

  openTourPopup(): void {
    setTimeout(() => {
      this.showTourPopup = true;
      this.showDropdown = false;
    }, 500);
  }

  closeTourPopup(): void {
    this.showTourPopup = false;
    this.authService.updateUserOnboard();
  }

  startFullTour(): void {
    this.closeTourPopup();
    this.tourService.startTour();
    this.auditLogService.logFeature({
      featureName: 'Tour guide',
      action: 'Start full tour',
      meta: { pageUrl: this.router.url },
      description: `Full tour of ${this.router.url} triggered`,
    });
  }

  openManageSubscriptions(): void {
    this.showDropdown = false;
    this.router.navigate(['/manage-subscriptions']);
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.notificationCheckSubscription) {
      this.notificationCheckSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.reAuthSubscription) {
      this.reAuthSubscription.unsubscribe();
    }
  }

  toggleDropdown(): void {
    this.showDropdown = !this.showDropdown;
  }

  goToProfile(): void {
    this.showDropdown = false;
    this.router.navigate(['/profile']);
  }

  goToNotifications(): void {
    this.router.navigate(['/notifications']);
  }

  toggleUploadProgress(): void {
    this.uploadProgressToggled.emit();
  }

  closeDropdown(): void {
    this.showDropdown = false;
  }

  openInvitePopup(): void {
    this.showInvitePopup = true;
  }

  closeInvitePopup(): void {
    this.showInvitePopup = false;
  }

  checkUnreadNotifications() {
    if (!this.isUnauthorizedLayout) {
      this.notificationsService.IsUnreadNotificationsExist().subscribe({
        next: (res) => {
          // Refresh token if subscription changed
          if (res.has_subscription_changed) {
            this.isSubscriptionPopupOpen = true;

            return;
          }

          const newUnreadCount = res.unread_count || 0;

          // Check if there are new notifications (count increased)
          if (newUnreadCount > this.unreadNotificationCount) {
            this.triggerShakeAnimation();
          }

          this.hasUnreadNotifications = newUnreadCount > 0;
          this.unreadNotificationCount = newUnreadCount;
        },
        error: (error) => {
          console.error('Error checking unread notifications:', error);
          // Stop polling on 401 so we don't keep hammering the token-refresh
          // lock in the auth interceptor. The poll will restart automatically
          // once the interceptor successfully refreshes the token.
          if (error?.status === 401) {
            this.stopNotificationChecking();
            this.authService.onTokenRefreshed$
              .pipe(
                filter((refreshed) => refreshed),
                takeUntil(this.destroy$),
              )
              .subscribe(() => this.startNotificationChecking());
          }
        },
      });
    }
  }

  triggerShakeAnimation() {
    this.shouldShake = true;
    // Remove the shake class after animation completes
    setTimeout(() => {
      this.shouldShake = false;
    }, 600); // Match the animation duration (0.6s)
  }

  stopNotificationChecking() {
    this.notificationCheckSubscription?.unsubscribe();
    this.notificationCheckSubscription = undefined;
  }

  startNotificationChecking() {
    if (!this.isUnauthorizedLayout && !this.notificationCheckSubscription) {
      this.notificationCheckSubscription = interval(5000)
        .pipe(
          // switchMap cancels the previous in-flight request on each tick,
          // preventing requests from stacking up if the API is slow.
          switchMap(() =>
            this.notificationsService.IsUnreadNotificationsExist(),
          ),
          takeUntil(this.destroy$),
        )
        .subscribe({
          next: (res) => {
            // Refresh token if subscription changed
            if (res.has_subscription_changed) {
              this.isSubscriptionPopupOpen = true;
              return;
            }
            const newUnreadCount = res.unread_count || 0;
            if (newUnreadCount > this.unreadNotificationCount) {
              this.triggerShakeAnimation();
            }
            this.hasUnreadNotifications = newUnreadCount > 0;
            this.unreadNotificationCount = newUnreadCount;
          },
          error: (error) => {
            console.error('Error in notification polling:', error);
            if (error?.status === 401) {
              this.stopNotificationChecking();
              this.authService.onTokenRefreshed$
                .pipe(
                  filter((refreshed) => refreshed),
                  takeUntil(this.destroy$),
                )
                .subscribe(() => this.startNotificationChecking());
            }
          },
        });
    }
  }

  onChangePassword() {
    this.showVerifyPopup = true;
  }

  onCancelVerify() {
    this.showVerifyPopup = false;
    this.isVerifying = false;
  }

  onOverlayClick(event: MouseEvent | KeyboardEvent) {
    if ((event.target as HTMLElement).classList.contains('verify-overlay')) {
      this.onCancelVerify();
    }
  }

  onVerifyClick() {
    this.isVerifying = true;

    this.loaderService.show();

    this.reAuthSubscription = this.authService
      .reAuthenticate()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide()),
      )
      .subscribe({
        error: () => {
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: 'Verification failed. Please try again.',
          });
        },
      });
  }

  reloadUI(): void {
    this.authService.refreshToken().subscribe({
      next: () => {
        this.isSubscriptionPopupOpen = false;
        window.location.reload();
      },
      error: (err) => {
        this.authService.logout().subscribe({
          error: (logoutError) =>
            console.error(
              'Logout failed after refresh token error:',
              logoutError,
            ),
        });
        console.error('Failed to refresh token', err);
      },
    });
  }
}
